import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip, Menu, MenuItem } from "@material-ui/core";
import { TOOTH_STATUSES } from "./DentalChartUtils";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import useDentalAccessibility from "./useDentalAccessibility";

const useStyles = makeStyles((theme) => ({
  tooth: {
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out, filter 0.2s ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    "&:hover": {
      transform: "scale(1.1)",
      filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))",
    },
  },
  toothNumber: {
    fontSize: "10px",
    fontWeight: "bold",
    fill: "#333",
    textAnchor: "middle",
    userSelect: "none",
  },
  statusIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorDot: {
    width: "12px",
    height: "12px",
    borderRadius: "2px",
    border: "1px solid #ccc",
  },
  toothWrapper: {
    display: "inline-block",
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: "4px",
    outline: "none",
    "&:focus-visible": {
      outline: "2px solid #1976d2",
      outlineOffset: "2px",
    },
  },
  toothWrapperSelected: {
    boxShadow: "0 0 0 2px #1976d2",
  },
}));

const ToothSVG = ({
  toothNumber,
  status,
  onClick,
  size = 40,
  isSelected = false,
  position = null,
  onFocus = null,
}) => {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  const [anchorEl, setAnchorEl] = useState(null);
  const toothRef = useRef(null);
  const [lastStatus, setLastStatus] = useState(status);

  const {
    handleToothKeyNavigation,
    getToothAriaProps,
    getToothAriaDescription,
    announceToothStatusChange,
  } = useDentalAccessibility();

  const currentStatus = TOOTH_STATUSES[status] || TOOTH_STATUSES.healthy;

  // Anunciar cambios de estado
  useEffect(() => {
    if (lastStatus !== status && lastStatus !== undefined) {
      announceToothStatusChange(toothNumber, lastStatus, status);
      setLastStatus(status);
    }
  }, [status, lastStatus, toothNumber, announceToothStatusChange]);

  const handleClick = (event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (newStatus) => {
    onClick?.(newStatus);
    handleClose();
  };

  const handleKeyDown = (event) => {
    handleToothKeyNavigation(event, toothNumber, () => {
      setAnchorEl(toothRef.current);
    });
  };

  const handleFocus = () => {
    onFocus?.(toothNumber);
  };

  // Generar atributos ARIA
  const ariaProps = getToothAriaProps(
    toothNumber,
    status,
    position,
    isSelected
  );

  // Definir forma del diente según su posición
  const getToothShape = (number) => {
    const num = parseInt(number);
    const lastDigit = num % 10;

    // Incisivos (1,2)
    if (lastDigit === 1 || lastDigit === 2) {
      return "M20 4 C24 4, 28 6, 28 12 C28 16, 26 20, 24 24 C22 28, 20 30, 20 30 C20 30, 18 28, 16 24 C14 20, 12 16, 12 12 C12 6, 16 4, 20 4 Z";
    }
    // Caninos (3)
    else if (lastDigit === 3) {
      return "M20 3 C25 3, 29 7, 29 13 C29 17, 27 21, 25 25 C23 29, 21 31, 20 31 C19 31, 17 29, 15 25 C13 21, 11 17, 11 13 C11 7, 15 3, 20 3 Z";
    }
    // Premolares (4,5)
    else if (lastDigit === 4 || lastDigit === 5) {
      return "M20 5 C25 5, 30 8, 30 14 C30 18, 28 22, 26 26 C24 30, 22 32, 20 32 C18 32, 16 30, 14 26 C12 22, 10 18, 10 14 C10 8, 15 5, 20 5 Z";
    }
    // Molares (6,7,8)
    else {
      return "M20 6 C26 6, 32 9, 32 15 C32 19, 30 23, 28 27 C26 31, 24 33, 20 33 C16 33, 14 31, 12 27 C10 23, 8 19, 8 15 C8 9, 14 6, 20 6 Z";
    }
  };

  return (
    <>
      <Tooltip
        title={`${t("Tooth")} ${toothNumber} - ${t(currentStatus.label)}`}
      >
        <div
          ref={toothRef}
          {...ariaProps}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onClick={handleClick}
          className={`${classes.toothWrapper}${isSelected ? ` ${classes.toothWrapperSelected}` : ""}`}
        >
          <svg
            width={size}
            height={size + 12}
            viewBox="0 0 40 46"
            className={classes.tooth}
            role="img"
            aria-hidden="true"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
            }}
          >
            {/* Forma del diente */}
            <path
              d={getToothShape(toothNumber)}
              fill={currentStatus.fill}
              stroke={currentStatus.stroke}
              strokeWidth="2"
            />

            {/* Número del diente */}
            <text x="20" y="40" className={classes.toothNumber}>
              {toothNumber}
            </text>
          </svg>

          {/* Descripción accesible oculta */}
          <div
            id={`tooth-${toothNumber}-description`}
            style={{
              position: "absolute",
              left: "-10000px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            {getToothAriaDescription(toothNumber, status, true)}
          </div>
        </div>
      </Tooltip>

      {/* Menú contextual para cambiar estado */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        MenuListProps={{
          "aria-label":
            t("tooth_status_menu_label", { number: toothNumber }) ||
            `Status options for tooth ${toothNumber}`,
          role: "menu",
        }}
      >
        {Object.entries(TOOTH_STATUSES).map(([statusKey, statusValue]) => (
          <MenuItem
            key={statusKey}
            onClick={() => handleStatusChange(statusKey)}
            selected={status === statusKey}
            role="menuitem"
            aria-describedby={`status-${statusKey}-description`}
          >
            <div className={classes.statusIndicator}>
              <div
                className={classes.colorDot}
                style={{
                  backgroundColor: statusValue.fill || statusValue.stroke,
                  borderColor: statusValue.stroke,
                }}
                aria-hidden="true"
              />
              {t(statusValue.label)}
              {status === statusKey && (
                <span
                  aria-label={t("current_status")}
                  style={{ marginLeft: 8 }}
                >
                  ✓
                </span>
              )}
            </div>

            {/* Descripción oculta para cada estado */}
            <div
              id={`status-${statusKey}-description`}
              style={{
                position: "absolute",
                left: "-10000px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              {t(`dental_status_${statusKey}_description`) ||
                `Set tooth ${toothNumber} status to ${statusValue.label}`}
            </div>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ToothSVG;
